// ForgotPassword.js
// Same split layout, palette and fields as the Sign In page — see AuthShell.
import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Alert, CircularProgress, Link as MuiLink, useTheme } from '@mui/material';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import axiosInstance from '../../utils/axiosInstance';
import AuthShell, {
    colors, fontFamily, makeInputSx, makePrimaryButtonSx, alertSx,
} from './AuthShell';

export default function ForgotPassword() {
    const theme = useTheme();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.title = 'Forgot Password | KlonIT Web Application';
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');
        setError('');
        setLoading(true);

        // axiosInstance carries the API base URL — a bare fetch('/forgot-password')
        // would post to the dashboard's own origin, not the backend.
        try {
            const { data } = await axiosInstance.post('/forgot-password', { email });
            setMessage(data.message);
        } catch (err) {
            setError(err?.response?.data?.message || 'Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            icon={<LockResetOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />}
            title="Reset Password"
            subtitle="We'll email you a link to get back into your account"
            footer={
                <>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        fontSize: '0.6875rem', color: colors.textTertiary, fontFamily,
                    }}>
                        <ShieldOutlinedIcon sx={{ fontSize: 13, color: colors.success }} />
                        Secure
                    </Box>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textTertiary }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                        Remembered it?{' '}
                        <MuiLink href="/Login" underline="hover"
                                 sx={{ color: theme.palette.primary.main, fontWeight: 600, fontFamily }}>
                            Back to Sign In
                        </MuiLink>
                    </Typography>
                </>
            }
        >
            {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}

            {message ? (
                <Box sx={{
                    p: 2.5, borderRadius: '16px', bgcolor: 'rgba(5, 150, 105, 0.08)',
                    border: '1px solid rgba(5, 150, 105, 0.25)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <MarkEmailReadOutlinedIcon sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: colors.textPrimary, fontFamily }}>
                            Check your inbox
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, mb: 2, fontFamily }}>
                        {message}
                    </Typography>
                    <Button
                        onClick={() => { setMessage(''); setEmail(''); }}
                        fullWidth
                        sx={{
                            py: '0.5rem', fontFamily, fontSize: '0.75rem', fontWeight: 500,
                            textTransform: 'none', color: colors.textTertiary,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                    >
                        Use a different email
                    </Button>
                </Box>
            ) : (
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth size="small" type="email" placeholder="Email address"
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        required disabled={loading} autoFocus
                        InputProps={{
                            startAdornment: <EmailOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} />,
                        }}
                        sx={{ ...makeInputSx(theme), mb: 1.5 }}
                    />
                    <Button type="submit" fullWidth disabled={loading || !email}
                            sx={makePrimaryButtonSx(theme)}>
                        {loading && <CircularProgress size={18} sx={{ color: '#fff' }} />}
                        {loading ? 'Sending…' : 'Send Reset Link'}
                    </Button>
                    <Typography sx={{
                        fontSize: '0.6875rem', color: colors.textTertiary, fontFamily,
                        textAlign: 'center', mt: 1.5,
                    }}>
                        The link expires in 30 minutes and works once.
                    </Typography>
                </form>
            )}
        </AuthShell>
    );
}
